import { useForkBranchCommand } from "commands/definitions/branch/useForkCommand";
import { useOpenPRCommand } from "commands/definitions/branch/useOpenPRCommand";
import { RegisterBranchCommands } from "commands/registrations/useRegisterBranchCommands";
import { useEditorStore } from "components/Editor/useEditorStore";
import { useConvertToRepositoryDialog } from "components/Sandbox/ConvertToRepository/ConvertToRepositoryDialog";
import { useEnvironmentInterface } from "environment-interface";
import { useBranch } from "features/branch";
import {
  useBranchEditor,
  useEditor,
  useEditorFeature,
  useSandboxEditor,
} from "features/editor/Feature";
import { useForkSandbox } from "features/forkSandbox";
import { useCurrentUser } from "hooks/api/useCurrentUser";
import { useImportProject } from "hooks/api/useImportProject";
import { useSession } from "hooks/api/useSession";
import {
  hasRepoWriteAccess,
  hasBranchWriteAccess,
} from "isomorphic/permission";
import { Button, Tooltip, DropdownButton } from "prism-react";

import { useActiveWorkspace } from "hooks/useActiveWorkspace";
import { useAnalytics } from "hooks/useAnalytics";
import { useEditorRouter } from "hooks/useEditorRouter";
import { useUserWorkspaces } from "hooks/useUserWorkspaces";

import { ForkErrorModal } from "./ForkErrorModal";
import { ForkContributionOptions, ForkRegularOptions } from "./ForkOptions";
import { ForkSandboxButton } from "./ForkSandboxButton";
import { ImportOption } from "./ImportOption";

const ImportProjectToWorkspace = ({
  repo,
  owner,
}: {
  repo: string;
  owner: string;
}) => {
  const { workspaces } = useUserWorkspaces();
  const importProject = useImportProject();
  const [, dispatchProject] = useEditorFeature();
  const user = useCurrentUser();

  const importProjectToWorkspace = async (
    workspaceId: string,
    workspaceName: string,
  ) => {
    const newProject = await importProject({
      owner,
      repo,
      workspaceId,
    });
    dispatchProject({
      type: "IMPORT_PROJECT",
      newProject,
      workspace: {
        id: workspaceId,
        name: workspaceName,
      },
    });
  };

  return (
    <DropdownButton
      buttonContent="Create branch"
      buttonTooltip={`Import ${repo}`}
      loading={!workspaces.length}
      variant="secondary"
      selfTrigger
    >
      {workspaces.map((workspace) => (
        <ImportOption
          currentUser={user}
          onImportClick={importProjectToWorkspace}
          workspace={{
            avatarUrl: workspace.avatarUrl || null,
            id: workspace.id,
            name: workspace.name,
          }}
        />
      ))}
    </DropdownButton>
  );
};

const BranchPrimaryCTA: React.FC = () => {
  const editor = useBranchEditor();
  const [branch, dispatchBranch] = useBranch();
  const { git } = useEditorStore();
  const track = useAnalytics();
  const activeWorkspace = useActiveWorkspace();
  const [session, sessionDispatch] = useSession();

  const { visibility } = useEnvironmentInterface();

  const openPRCommand = useOpenPRCommand();

  const createBranchFromCurrent = useForkBranchCommand({
    target: "CURRENT",
  });

  const createContributionBranch = useForkBranchCommand({
    target: "CONTRIBUTION_BRANCH",
  });

  const isDefaultBranch =
    editor.type === "branch" && editor.branch === editor.defaultBranchName;
  const canCreateContributionBranch =
    !editor.allowedCapabilities.branch && !editor.isPrivate;
  const hasWriteAccess = hasBranchWriteAccess(editor.authorization);

  const canImportBranch =
    !hasWriteAccess &&
    hasRepoWriteAccess(editor.repoPermission) &&
    activeWorkspace;

  const createBranchProps = () => {
    if (session.state === "UNAUTHENTICATED") {
      return {
        loading: false,
        onClick: () => {
          sessionDispatch.signIn();
        },

        buttonContent: "Sign In",
        buttonTooltip: "Sign in to create a branch",
      };
    }

    const loadingState =
      branch.state === "CREATING_BRANCH" ||
      branch.state === "CREATING_DRAFT_BRANCH" ||
      branch.state === "FORKING_BRANCH" ||
      Boolean(git.pendingCommit) ||
      git.commitMessage.status === "GETTING_SUGGESTION";

    /**
     * openPRCommand will exist if there's an open
     * pr, local changes or commits ahead.
     */
    if (openPRCommand) {
      return {
        loading: loadingState,
        onClick: openPRCommand.perform,
        buttonContent: openPRCommand.name,
        buttonTooltip: openPRCommand.subtitle,
      };
    }

    /**
     * Contribution branch
     */
    if (canCreateContributionBranch) {
      return {
        loading: loadingState,
        onClick: () => {
          createContributionBranch.perform();
        },

        buttonContent: "Create branch",
        buttonTooltip: createContributionBranch.name,
      };
    }

    /**
     * Fork current branch
     */
    return {
      loading: loadingState,
      disabled: !editor.allowedCapabilities.branch,
      onClick: () => {
        createBranchFromCurrent.perform();
      },

      buttonContent: "Create branch",
      buttonTooltip: createBranchFromCurrent.name,
    };
  };

  const renderDropDownOptions = () => {
    if (canCreateContributionBranch) {
      return <ForkContributionOptions />;
    }

    if (!editor.allowedCapabilities.branch) {
      return null;
    }

    if (isDefaultBranch) {
      return null;
    }

    return <ForkRegularOptions />;
  };

  if (visibility.isInIframe) {
    // We're in an embed mode
    return (
      <Button
        as="a"
        href={document.location.href}
        onClick={() => {
          track("embed_open_editor");
        }}
        rel="noopener"
        target="_blank"
      >
        Open Editor
      </Button>
    );
  }

  return (
    <>
      <RegisterBranchCommands />
      {branch.state === "BRANCH_CREATE_ERROR" ? (
        <ForkErrorModal
          message={branch.message}
          onDismiss={() => {
            dispatchBranch({
              type: "DISMISS_ERROR",
            });
          }}
          workspaceIdFromFork={branch.workspaceId}
        />
      ) : null}
      {canImportBranch ? (
        <ImportProjectToWorkspace owner={editor.owner} repo={editor.repo} />
      ) : (
        <DropdownButton variant="secondary" {...createBranchProps()}>
          {renderDropDownOptions()}
        </DropdownButton>
      )}
    </>
  );
};

const SandboxPrimaryCTA: React.FC = () => {
  const [fork, dispatchFork] = useForkSandbox();

  const { open } = useConvertToRepositoryDialog();
  const router = useEditorRouter();

  const editor = useSandboxEditor();
  const track = useAnalytics();
  const { visibility } = useEnvironmentInterface();

  const createRepo = router.queryParams.createRepo.get();

  if (visibility.isInIframe) {
    // We're in an embed mode
    return (
      <Button
        as="a"
        href={document.location.href}
        onClick={() => {
          track("embed_open_editor");
        }}
        rel="noopener"
        target="_blank"
      >
        Open Editor
      </Button>
    );
  }

  return (
    <>
      {fork.state === "CREATE_FORK_ERROR" ? (
        <ForkErrorModal
          message={fork.message}
          onDismiss={() => {
            dispatchFork({
              type: "DISMISS_ERROR",
            });
          }}
          workspaceIdFromFork={fork.workspaceId}
        />
      ) : null}

      <ForkSandboxButton dispatchFork={dispatchFork} fork={fork} />

      {createRepo && (
        <Tooltip content="Access to terminals, VMs and more">
          <Button
            css={{ paddingLeft: "$3", paddingRight: "$3" }}
            icon="server"
            onClick={() => {
              open();
              track("create_repo_from_template", {
                id: editor.id,
                title: editor.title,
                location: "primary-button",
              });
            }}
            variant="primary"
          >
            Create repository
          </Button>
        </Tooltip>
      )}
    </>
  );
};

export const PrimaryCTA: React.FC = () => {
  const editor = useEditor();

  if (editor.type === "sandbox") {
    return <SandboxPrimaryCTA />;
  }

  return <BranchPrimaryCTA />;
};
